import { useMemo, useState } from 'react';
import {
  DataGridPremium,
  useGridApiRef,
  GRID_AGGREGATION_FUNCTIONS,
  useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium';
import {
  Box,
  Tooltip,
  IconButton,
  Typography,
  Avatar,
  Badge,
} from '@mui/material';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import DataGridNoRowsCustomOverlay from '../../../components/datagrid/DataGridNoRowsOverlay';
import { getTypeColor, getTypeIcon } from '../../../utils/nodeTypeMapper';
import { useTranslation } from 'react-i18next';

import { Chip } from '@mui/material';
import { formatNumber } from '@/utils/formatters';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import { useSelector } from 'react-redux';
import { selectTimeRange } from '@/features/energyxplorer/redux/timeRangeSlice';
import { isOutOfBounds } from '@/features/energyxplorer/utils/timestampsSummaryHelpers';
import { useTheme } from '@emotion/react';

const InfrastructureNodeDataGrid = ({ energyOverviewDataResult }) => {
  const apiRef = useGridApiRef();

  const { t } = useTranslation('energyxplorer-energy-overview-tab');

  const [expandedGroups, setExpandedGroups] = useState(new Set()); // Track expanded groups
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortModel, setSortModel] = useState([
    { field: 'consumption', sort: 'desc' },
  ]);
  const timeRange = useSelector(selectTimeRange);

  const handleRowToggle = (rowId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  // Track expanded/collapsed state dynamically
  const handleRowExpansion = (row) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(row)) {
        newSet.delete(row); // Remove if already expanded
      } else {
        newSet.add(row); // Add when expanded
      }
      return newSet;
    });
  };

  // Updated function to determine whether "Total" is in the inline group or footer
  const getAggregationPosition = (groupNode) =>
    expandedGroups.has(groupNode.groupingKey) ? 'footer' : 'inline';

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {},
  });

  const columns = [
    {
      field: 'icon',
      headerName: t('data_grid_infrastructure_columns.boards.header'),
      // flex: 1,
      width: 150,
      renderCell: (params) => {
        const row = params.row;
        const isExpanded = expandedRows.has(row.id);
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Tooltip title={params.value} placement="top">
              <Avatar
                variant="round"
                size="small"
                sx={{
                  backgroundColor: getTypeColor(params.value) || '#555',
                  color: '#fff',
                  height: 32,
                  width: 32,
                  fontSize: 16,
                }}
              >
                <i className={`fas ${getTypeIcon(params.value)}`}></i>
              </Avatar>
            </Tooltip>
            {isOutOfBounds(params.row.timestampsSummary, timeRange) && (
              <Tooltip
                placement="top"
                title={t(
                  'data_grid_infrastructure_columns.timestamp_summary_warning',
                )}
              >
                <Badge
                  badgeContent={
                    <WarningRoundedIcon fontSize="small" color="warning" />
                  }
                  sx={{ ml: 1 }}
                >
                  <CalendarTodayRoundedIcon fontSize="small" />
                </Badge>
              </Tooltip>
            )}
            {row.hasChildren && (
              <IconButton
                size="small"
                onClick={() => handleRowToggle(row.id)}
                sx={{ ml: 2 }}
              >
                {isExpanded ? (
                  <ExpandLessRoundedIcon />
                ) : (
                  <ExpandMoreRoundedIcon />
                )}
              </IconButton>
            )}
            {/* <Typography sx={{ marginLeft: 1 }}>{params.value}</Typography> */}
          </Box>
        );
      },
    },
    {
      field: 'name',
      headerName: t('data_grid_infrastructure_columns.name.header'),
      width: 300,
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Typography>{params.value}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'consumption',
      headerName: t('data_grid_infrastructure_columns.consumption.header'),
      flex: 1,
      minWidth: 150,
      type: 'number',
      groupable: false,
      aggregable: true,
      headerAlign: 'left',

      aggregationFn: (params) => {
        return params.values.reduce((sum, val) => sum + (val || 0), 0);
      },

      valueFormatter: (params) => {
        return params.value != null ? `${params.value} kWh` : '-';
      },

      renderCell: (params) => {
        const { value } = params;
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Typography>
              {value != null ? `${formatNumber(value)} kWh` : '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'accounted',
      headerName: t('data_grid_infrastructure_columns.breakdown_share.header'),
      flex: 1,
      minWidth: 150,
      type: 'number',
      groupable: false,
      aggregable: false,
      headerAlign: 'left',

      valueFormatter: (params) => {
        return params.value != null ? `${params.value} kWh` : '-';
      },

      renderCell: (params) => {
        const { row, value } = params;
        return row.accountedPercentage !== 0 ? (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Typography>
              {value !== null ? `${formatNumber(value)} kWh` : '100%'}
            </Typography>
            <CoverageChip
              renderWarning={isOutOfBounds(row.timestampsSummary, timeRange)}
              percentage={row.accountedPercentage}
            />
          </Box>
        ) : (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Typography
              color="text.secondary"
              fontStyle="italic"
              fontSize="0.9em"
            >
              Ikke neddelt yderligere
            </Typography>
          </Box>
        );
      },
    },
  ];

  const rows = useMemo(() => {
    if (!energyOverviewDataResult) return [];

    const rows = [];

    energyOverviewDataResult.DistributionBoardsSummary.forEach(
      ({ MainDistributionBoard, SubDistributionBoards }) => {
        rows.push({
          id: MainDistributionBoard.Id,
          parentId: null,
          name: MainDistributionBoard.Name,
          icon: 'Tavle',
          consumption: MainDistributionBoard.TotalEnergyUsage,
          accounted: MainDistributionBoard.ChildEnergyUsage,
          accountedPercentage: MainDistributionBoard.ChildEnergyUsagePercentage,
          unaccounted: MainDistributionBoard.NotAnalysedEnergyUsage,
          unaccountedPercentage:
            MainDistributionBoard.NotAnalysedEnergyUsagePercentage,
          hasChildren: SubDistributionBoards.length > 0,
          timestampsSummary: MainDistributionBoard.TimestampsSummary,
        });

        SubDistributionBoards.forEach((sub) => {
          rows.push({
            id: sub.Id,
            parentId: MainDistributionBoard.Id,
            name: sub.Name,
            icon: 'Undertavle',
            consumption: sub.TotalEnergyUsage,
            accounted: sub.ChildEnergyUsage,
            accountedPercentage: sub.ChildEnergyUsagePercentage,
            unaccounted: sub.NotAnalysedEnergyUsage,
            unaccountedPercentage: sub.NotAnalysedEnergyUsagePercentage,
            timestampsSummary: sub.TimestampsSummary,
          });
        });
      },
    );

    return rows;
  }, [energyOverviewDataResult]);

  // Define any handlers
  const handleRowClick = (params) => {
    console.log('Infrastructure row clicked:', params.row);
  };

  const sortedParentRows = useMemo(() => {
    let parents = rows.filter((row) => row.parentId === null);

    // If no sort, return as is
    if (sortModel.length === 0) return parents;

    // Apply sorting based on the first sort field
    const { field, sort } = sortModel[0];
    return [...parents].sort((a, b) => {
      const aValue = a[field] ?? '';
      const bValue = b[field] ?? '';

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sort === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        return sort === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }
    });
  }, [rows, sortModel]);

  const visibleRows = useMemo(() => {
    const result = [];

    sortedParentRows.forEach((parent) => {
      result.push(parent); // Always include parent

      if (expandedRows.has(parent.id)) {
        // Include children directly under parent if expanded
        const children = rows.filter((row) => row.parentId === parent.id);
        result.push(...children);
      }
    });

    return result;
  }, [sortedParentRows, rows, expandedRows]);

  return (
    <DataGridPremium
      apiRef={apiRef}
      rows={visibleRows}
      columns={columns}
      hideFooter
      sortingMode="server"
      onRowClick={handleRowClick}
      getAggregationPosition={(model) => getAggregationPosition(model)} // Dynamically place Total
      onRowExpansionModelChange={(model) => handleRowExpansion(model)}
      initialState={initialState}
      disableRowSelectionOnClick
      groupingColDef={{ headerName: 'Group', width: 120 }}
      sortModel={sortModel}
      onSortModelChange={(model) => setSortModel(model)}
      getRowClassName={(params) =>
        params.row.parentId ? 'sub-row' : 'parent-row'
      }
      aggregationFunctions={{
        ...GRID_AGGREGATION_FUNCTIONS,
      }}
      slots={{
        noRowsOverlay: DataGridNoRowsCustomOverlay,
      }}
      slotProps={{
        noRowsOverlay: {
          rows: t('data_grid_infrastructure_columns.no_rows_overlay'),
          isLoading: false,
        },
      }}
      sx={{
        // flexGrow: 1,
        borderRadius: 2,
        borderColor: 'divider',
        // overflow: 'auto',
        // minHeight: 250,
        maxHeight: 400,
        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold' },
        '& .MuiDataGrid-footerContainer': {
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? '#fff' : '#121212',
          borderTop: '1px solid',
          borderColor: 'divider',
        },
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
          {
            outline: 'none',
          },
        '& .MuiDataGrid-row[data-rowid^="Sum-"]': {
          backgroundColor: '#f0f0f0',
          fontWeight: 'bold',
        },
        '& .MuiDataGrid-main': {
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? '#fff' : '#121212',
        },
        '& .MuiDataGrid-row.sub-row': {
          '& .MuiDataGrid-cell': {
            pl: 4, // padding-left: 32px (4 * 8px grid spacing)
            fontSize: '0.9rem',
            opacity: 0.85,
          },
        },
        '& .MuiDataGrid-row.parent-row': {
          '& .MuiDataGrid-cell': {
            fontWeight: 'bold',
          },
        },
        '& .MuiDataGrid-row[data-rowid^="IkkeAnalyseret"]': {
          backgroundColor: 'red',
          fontWeight: 'bold',
        },
      }}
    />
  );
};

export default InfrastructureNodeDataGrid;
const CoverageChip = ({ percentage, renderWarning }) => {
  const value = parseFloat(percentage.toFixed(1));
  const { t } = useTranslation('energyxplorer-energy-overview-tab');
  return (
    <>
      {renderWarning ? (
        <Tooltip
          placement="top"
          title={t(
            'data_grid_infrastructure_columns.timestamp_summary_warning',
          )}
        >
          <Box>
            <HashedChip label={`${value}%`} />
          </Box>
        </Tooltip>
      ) : (
        <Chip
          label={`${value}%`}
          // color={colorKey}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 'bold',
            ml: 1,
            minWidth: 60,
            justifyContent: 'center',
            // backgroundColor: background,
          }}
        />
      )}
    </>
  );
};
function HashedChip({ label = '0' }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const color = isDarkMode ? 'gray' : 'lightgray';

  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 'bold',
        color: isDarkMode ? 'white' : 'black',
        // engraved hash background
        backgroundImage: `repeating-linear-gradient(
          45deg,
          ${color},
          ${color} 6px,
          transparent 6px,
          transparent 12px
        )`,
        backgroundColor: `${color}22`,
        opacity: 0.8,
        ml: 1,

        border: `1px solid ${color}`,
      }}
    />
  );
}
